import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogBody } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { WorkerAniAPI } from "@/services/api";
import type { WorkerCard } from "@/services/api/worker-ani/types";
import { Avatar } from "../ui/Avatar";
import { Badge } from "@/components/ui/badge/Badge";
import WorkerAvatar from "./WorkerAvatar";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessage } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

interface WorkerCardDialogProps {
  workerId: string;
  open: boolean;
  onClose: () => void;
  rules?: string[];
}

export default function WorkerCardDialog({
  workerId,
  open,
  onClose,
  rules,
}: WorkerCardDialogProps) {
  const [workerCard, setWorkerCard] = useState<WorkerCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWorkerCard = async () => {
      if (!workerId) return;
      try {
        setIsLoading(true);
        const card = await WorkerAniAPI.getWorkerCard(workerId);
        setWorkerCard(card);
      } catch (error) {
        console.error("Failed to fetch worker card:", error);
        setError("Failed to load worker details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkerCard();
  }, [workerId]);

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>
        <div className="flex items-center gap-3">
          <WorkerAvatar seed={workerCard?.name || workerId} size={48} />
          <span>{workerCard?.name || "Worker Card"}</span>
        </div>
      </DialogTitle>
      <DialogBody>
        {isLoading && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
          </div>
        )}
        {error && (
          <div className="text-red-500">Error loading worker card: {error}</div>
        )}
        {!isLoading && !error && workerCard && (
          <div className="space-y-8">
            {workerCard.job_title && (
              <div>
                <h3 className="font-semibold text-gray-700 dark:text-gray-300">
                  Job Title
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {workerCard.job_title}
                </p>
              </div>
            )}
            {workerCard.bio && (
              <div>
                <h3 className="font-semibold text-gray-700 dark:text-gray-300">
                  Bio
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {workerCard.bio}
                </p>
              </div>
            )}
            {workerCard.skills_display &&
              workerCard.skills_display.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300">
                    Skills
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {workerCard.skills_display.map((skill, index) => (
                      <Badge key={index} color="blue">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            {rules && rules.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-700 dark:text-gray-300 mt-4">
                  Worker Rules
                </h3>
                <ul className="list-disc list-outside pl-5 mt-2 space-y-1 text-gray-600 dark:text-gray-400">
                  {rules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            <Button
              onClick={() => {
                navigate(`/worker/${workerId}`);
                onClose();
              }}
              className="flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faMessage} />
              Chat with {workerCard?.name || 'Worker'}
            </Button>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
